import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Calendar } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  summarizeUpcomingDates,
  buildAddToCalendarUrl,
  SHORT_LABEL,
} from "../../utils/importantDates";

const URGENCY_CLASS = {
  soon: "text-amber-600",
  future: "text-gray-600",
};
const ALL_DEALS_ROLES = ["Admin", "Supervisor", "Accountant"];

function formatShortDate(dateObj) {
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${dateObj.getFullYear()}`;
}

function daysAwayCopy(daysAway) {
  if (daysAway === 0) return "Today";
  if (daysAway === 1) return "In 1 Day";
  return `In ${daysAway} Days`;
}

export default function ImportantDatesWidget() {
  const [{ user, deals }] = useStateValue();
  const navigate = useNavigate();

  const scopedDeals = useMemo(() => {
    if (!Array.isArray(deals)) return [];
    if (ALL_DEALS_ROLES.includes(user?.role)) return deals;
    return deals.filter((d) => d.agentData === user?.id);
  }, [deals, user]);

  const rows = useMemo(
    () => summarizeUpcomingDates(scopedDeals, { windowDays: 14 }),
    [scopedDeals],
  );

  const handleRowClick = (deal) => {
    if (!deal?.createdat) return;
    navigate(`/dashboard/deals-detail/${deal.createdat}`);
  };

  const handleAddToCalendar = (e, row) => {
    e.stopPropagation();
    const url = buildAddToCalendarUrl({
      title: `${SHORT_LABEL[row.field]} — ${row.deal?.propertyaddress || "Deal"}`,
      isoDate: row.isoDate,
      description: `Property: ${row.deal?.propertyaddress || ""}${
        row.deal?.clientLastName ? ` — ${row.deal.clientLastName}` : ""
      }`,
    });
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-900">
          Important Dates Coming Up
        </div>
        <div className="text-xs text-gray-400">Next 14 Days</div>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-500 text-center">
          No important dates in the next 14 days.
        </div>
      ) : (
        rows.map((row, i) => (
          <div
            key={`${row.deal.createdat}-${row.field}`}
            onClick={() => handleRowClick(row.deal)}
            className={`px-4 py-2.5 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${
              i < rows.length - 1 ? "border-b border-gray-100" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {row.deal.propertyaddress || "(no address)"}
                {row.deal.clientLastName && ` · ${row.deal.clientLastName}`}
              </div>
              <div
                className={`text-xs mt-0.5 ${
                  URGENCY_CLASS[row.urgency] || "text-gray-500"
                }`}
              >
                {SHORT_LABEL[row.field]} · {formatShortDate(row.dateObj)} ·{" "}
                {daysAwayCopy(row.daysAway)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={(e) => handleAddToCalendar(e, row)}
                className="text-gray-400 hover:text-blue-600 p-1"
                aria-label="Add to Google Calendar"
                title="Add to Google Calendar"
              >
                <Calendar size={16} />
              </button>
              <ChevronRight size={16} className="text-gray-300" />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
